/**
 * Module Federation Runtime Plugin — Graceful Error Handling + Diagnostics
 *
 * Two responsibilities:
 *
 *   1. Catch remote module load failures so the host continues working
 *      when a remote (laminar, metriq) is unavailable. Only
 *      the remote's pages show an error — the rest of the host stays
 *      functional. Per-page ErrorBoundaries / useRemoteModule's catch
 *      handler render the UI fallback.
 *
 *   2. Log share-scope negotiation so the next time we hit a "host +
 *      remote disagree about a shared singleton" failure, we can see
 *      WHICH package and WHICH versions are at play. Previously, that
 *      class of bug surfaced only as React error #527 with no context.
 *
 * Boot-time eager remote loading is disabled via `shareStrategy:
 * 'loaded-first'` in vite.config.ts — so by design, errorLoadRemote here
 * only fires during *user-triggered* navigation to a remote-backed route
 * (via useRemoteModule). The per-route fallback then handles UI degradation.
 */
import type { ModuleFederationRuntimePlugin } from '@module-federation/runtime-core';

/**
 * Purge the MF runtime's global promise cache for a remote so a
 * subsequent import() triggers a fresh network fetch. Without this, one
 * failed load stays cached and blocks every retry.
 */
function purgeGlobalLoadingCache(remoteName: string): void {
  const globalLoading = (
    globalThis as typeof globalThis & {
      __GLOBAL_LOADING_REMOTE_ENTRY__?: Record<string, unknown>;
    }
  ).__GLOBAL_LOADING_REMOTE_ENTRY__;

  if (!globalLoading) return;

  for (const key of Object.keys(globalLoading)) {
    if (key.startsWith(`${remoteName}:`)) {
      delete globalLoading[key];
    }
  }
}

// Toggle for the share-scope diagnostic logs. Info-level so they appear
// without enabling DevTools Verbose. Flip to `false` to mute if the noise
// outweighs the value in prod.
const SHARE_DEBUG = true;

function logShareDebug(event: string, details: Record<string, unknown>): void {
  if (!SHARE_DEBUG) return;

  console.info(`[mf-share] ${event}`, details);
}

const errorHandlerPlugin = (): ModuleFederationRuntimePlugin => ({
  name: 'mf-error-handler',

  // ── Remote-load failures ─────────────────────────────────────────────
  errorLoadRemote({ id, error, lifecycle }) {
    const msg = error instanceof Error ? error.message : String(error);
    console.warn(
      `[Module Federation] Failed to load remote module "${id}" (lifecycle=${lifecycle}): ${msg}`
    );

    // Extract remote name from id (e.g. "metriqApp/MetriqApp" → "metriqApp")
    const remoteName = id.split('/')[0];
    if (remoteName) {
      purgeGlobalLoadingCache(remoteName);
    }

    // Don't return a fallback — let the error propagate to
    // useRemoteModule's catch handler / ErrorBoundary for that page.
  },

  // ── Share-scope diagnostics ──────────────────────────────────────────
  // These fire during share negotiation. They give visibility into:
  //   • who registered a share and what version they offered
  //   • which version was chosen when multiple are available
  //   • which share is being requested at any given moment
  //
  // Filter the console for "[mf-share]" to see the full negotiation trail.

  beforeRegisterShare(args) {
    const a = args as {
      pkgName?: string;
      shared?: {
        version?: string;
        shareConfig?: { requiredVersion?: string; singleton?: boolean };
      };
      origin?: { options?: { name?: string } };
    };
    logShareDebug('registerShare', {
      pkg: a.pkgName,
      from: a.origin?.options?.name,
      version: a.shared?.version,
      required: a.shared?.shareConfig?.requiredVersion,
      singleton: a.shared?.shareConfig?.singleton,
    });
    return args;
  },

  async beforeLoadShare(args) {
    const a = args as {
      pkgName?: string;
      shareInfo?: {
        version?: string;
        shareConfig?: { requiredVersion?: string };
      };
      origin?: { options?: { name?: string } };
    };
    logShareDebug('loadShare', {
      pkg: a.pkgName,
      from: a.origin?.options?.name,
      version: a.shareInfo?.version,
      required: a.shareInfo?.shareConfig?.requiredVersion,
    });
    return args;
  },

  resolveShare(args) {
    const a = args as { pkgName?: string; version?: string; scope?: string };
    logShareDebug('resolveShare', {
      pkg: a.pkgName,
      version: a.version,
      scope: a.scope,
    });
    return args;
  },
});

export default errorHandlerPlugin;
